import { ColorDot } from "./ColorDot";

type BadgeColor =
  | "default"
  | "gray"
  | "brown"
  | "orange"
  | "yellow"
  | "green"
  | "blue"
  | "purple"
  | "pink"
  | "red";

interface PropertyBadgeProps {
  label: string;
  color?: BadgeColor;
  showDot?: boolean;
  size?: "sm" | "md";
  /** Use pill shape instead of rounded rectangle */
  pill?: boolean;
  className?: string;
}

// CSS variable-based colors for design system consistency
const colorConfig: Record<BadgeColor, { bg: string; text: string; dot: string }> = {
  default: {
    bg: "var(--property-default-bg)",
    text: "var(--property-default-text)",
    dot: "#9ca3af",
  },
  gray: {
    bg: "var(--property-gray-bg)",
    text: "var(--property-gray-text)",
    dot: "#9ca3af",
  },
  brown: {
    bg: "var(--property-brown-bg)",
    text: "var(--property-brown-text)",
    dot: "#d97706",
  },
  orange: {
    bg: "var(--property-orange-bg)",
    text: "var(--property-orange-text)",
    dot: "#ea580c",
  },
  yellow: {
    bg: "var(--property-yellow-bg)",
    text: "var(--property-yellow-text)",
    dot: "#ca8a04",
  },
  green: {
    bg: "var(--property-green-bg)",
    text: "var(--property-green-text)",
    dot: "#22c55e",
  },
  blue: {
    bg: "var(--property-blue-bg)",
    text: "var(--property-blue-text)",
    dot: "#3b82f6",
  },
  purple: {
    bg: "var(--property-purple-bg)",
    text: "var(--property-purple-text)",
    dot: "#a855f7",
  },
  pink: {
    bg: "var(--property-pink-bg)",
    text: "var(--property-pink-text)",
    dot: "#ec4899",
  },
  red: {
    bg: "var(--property-red-bg)",
    text: "var(--property-red-text)",
    dot: "#ef4444",
  },
};

const sizeStyles = {
  sm: "px-1.5 py-0.5 text-xs gap-1",
  md: "px-2 py-1 text-sm gap-1.5",
};

export function PropertyBadge({
  label,
  color = "default",
  showDot = false,
  size = "md",
  pill = false,
  className = "",
}: PropertyBadgeProps) {
  const config = colorConfig[color];

  return (
    <span
      className={`
        inline-flex items-center font-medium whitespace-nowrap
        transition-colors duration-150
        ${sizeStyles[size]}
        ${pill ? "rounded-full" : "rounded"}
        ${className}
      `}
      style={{
        backgroundColor: config.bg,
        color: config.text,
      }}
    >
      {showDot && <ColorDot color={config.dot} size="sm" />}
      {label}
    </span>
  );
}

export { type PropertyBadgeProps, type BadgeColor };
